import React, {useState, useMemo, useCallback} from 'react'
import { UserListHead, UserListToolbar } from 'components/_dashboard/user'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import TablePagination from '@material-ui/core/TablePagination'
import Stack from '@material-ui/core/Stack'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Box from '@material-ui/core/Box'

import moment from 'moment-timezone'

import TABLEHEAD from './config/tablehead'
import Editable from 'components/editable/number'
import EditableText from 'components/editable/text'

import UsersAPI from 'utils/api/users'

import useUpdateScheduleValues from 'hooks/useUpdateScheduleValues'

moment().tz('Asia/Manila').format()

const formatTableNumber = (value) => {
	if (value === null || value === undefined || value === '') return '0.00';
	const num = typeof value === 'string' ? parseFloat(value) : Number(value);
	return isNaN(num) ? '0.00' : (num < 0 ? '0.00' : num.toFixed(2));
};

const UserBreaklistTable = ({onEditValues = () => {}, breaklist = [], breaklistId = '', onSave = () => {}}) => {
	const {
		updateSchedValues, 
		scheduleValuesOf,
		getUpdatedScheduleValues,
		resetScheduleValues
	} = useUpdateScheduleValues()

	let [pageNumber, setPageNumber] = useState(0)
	let [rowsPerPage, setRowsPerPage] = useState(5)
	let [order, setOrder] = useState('asc') //asc or desc
	let [orderBy, setOrderBy] = useState('')
	
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const changePageNumber  = useCallback((_e, newPage) => {
		setPageNumber(newPage)
	} ,[])

	const changeNumberOfRowsPerPage = useCallback((e) => {
		setRowsPerPage(parseInt(e.target.value, 10))
		setPageNumber(0)
	}, [])

	const changeOrderBy = useCallback((_, property) => {
		const isAsc = orderBy === property && order === 'asc'
    	setOrder(isAsc ? 'desc' : 'asc')
    	setOrderBy(property)
	}, [])


	//save schedule values to backend, updates payroll entry
	/**
	 * {
	 	uid, date, breaklistId{
			otHours, nightdiff, hoursTardy, restday
	 	}
	 * }
	 * 
	 * */
	const saveScheduleValues = useCallback( async (updates) => {
		
		console.log('saving schedule values',updates)
		try{
			let request = await UsersAPI.patch_schedule_employee(updates)
			console.log("check",request)
			if(!request.data.success){
				alert('Error saving schedule values')
			}
			console.log(request)
		}catch(err){
			console.log(err)
		}
	}, [])

	/**
	 *  {
	 	 remarkID, remarks
	 * 	}
	 * 
	 * */
	const saveRemarks = useCallback(async (id, remark) => {
		try{
			let request = await UsersAPI.update_schedule_remarks({
				id, remark, 
				breaklistId
			})

			console.log(request)
		}catch(err){
			console.log(err)
		}

	}, [])


	const computeTotals = useCallback(() => {
		let totals = breaklist.reduce((acc, {_id, timeIn, timeOut, hoursTardy, overtime, nightdiff, hourswork, rd, date}) => {
			let remarkId = `${_id}-${date}-${timeIn}-${timeOut}`;
			
			// Skip records where hourswork is absent or 0
			if (!hourswork || Number(hourswork) <= 0) {
				return acc;
			}

			// Properly handle number conversions with null checks
			let computedHoursTardy = scheduleValuesOf(remarkId)?.['hoursTardy'] !== undefined ? 
				Number(scheduleValuesOf(remarkId)?.['hoursTardy']) : 
				Number(hoursTardy) || 0;

			let computedOvertime = scheduleValuesOf(remarkId)?.['overtime'] !== undefined ? 
				Number(scheduleValuesOf(remarkId)?.['overtime']) : 
				Number(overtime) || 0;

			let computedNightdiff = scheduleValuesOf(remarkId)?.['nightdiff'] !== undefined ? 
				Number(scheduleValuesOf(remarkId)?.['nightdiff']) : 
				Number(nightdiff) || 0;

			let computedRestday = scheduleValuesOf(remarkId)?.['rd'] !== undefined ? 
				Number(scheduleValuesOf(remarkId)?.['rd']) : 
				Number(rd) || 0;

			// Ensure no negative values
			computedHoursTardy = computedHoursTardy < 0 ? 0 : computedHoursTardy;
			computedOvertime = computedOvertime < 0 ? 0 : computedOvertime;
			computedNightdiff = computedNightdiff < 0 ? 0 : computedNightdiff;
			computedRestday = computedRestday < 0 ? 0 : computedRestday;

			return {
				hoursTardy: acc.hoursTardy + computedHoursTardy,
				otHours: acc.otHours + computedOvertime,
				nightdiff: acc.nightdiff + computedNightdiff,
				rd: acc.rd + computedRestday
			};
		}, {hoursTardy: 0, otHours: 0, nightdiff: 0, rd: 0});

		// Format all totals to 2 decimal places
		return {
			hoursTardy: Number(totals.hoursTardy.toFixed(2)),
			otHours: Number(totals.otHours.toFixed(2)),
			nightdiff: Number(totals.nightdiff.toFixed(2)),
			rd: Number(totals.rd.toFixed(2))
		};
	}, [breaklist, scheduleValuesOf]);

	// Calculate totals for mobile view
	const totals = useMemo(() => {
		return computeTotals();
	}, [breaklist, scheduleValuesOf]);

	if (isMobile) {
		return (
			<>
				<Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center' }}>
					{breaklist.length} Records
				</Typography>
				<Grid container spacing={2} sx={{ mt: 1, pb: 2 }}>
					{breaklist.slice(pageNumber * rowsPerPage, pageNumber * rowsPerPage + rowsPerPage).map((row, index) => {
						const {
							_id,
							name,
							date,
							from,
							to,
							timeIn,
							timeOut,
							hoursTardy,
							overtime,
							nightdiff,
							hourswork,
							rd,
							legalholiday,
							specialholiday, 
							remarks
						} = row;

						let id = `${_id}/${date}/${timeIn}/${timeOut}`;
						let remarkId = `${_id}-${date}-${timeIn}-${timeOut}`;
						
						const formattedDate = moment(date).format('YYYY-MM-DD');
						
						let tardy = scheduleValuesOf(remarkId)?.['hoursTardy'] || hoursTardy || 0;
						let ot = scheduleValuesOf(remarkId)?.['overtime'] || overtime || 0;
						let nd = scheduleValuesOf(remarkId)?.['nightdiff'] || nightdiff || 0;
						let restday = scheduleValuesOf(remarkId)?.['rd'] || rd || 0;
						
						// Check if editing should be disabled (absent or hourswork <= 0)
						const isEditingDisabled = !timeIn || !timeOut || !hourswork || Number(hourswork) <= 0;
						
						// Text for hours worked
						let workHours = !timeIn && !timeOut && hourswork <= 0 ? 
							"Absent" : Number(hourswork) <= 0 ? 
							formatTableNumber(hourswork) : formatTableNumber(hourswork);

						return (
							<Grid item xs={12} key={`${_id}-${index}`}>
								<Card sx={{ 
									boxShadow: 2,
									borderRadius: 2,
									bgcolor: 'background.paper',
									'&:hover': {
										boxShadow: 5
									}
								}}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											{formattedDate}
										</Typography>
										
										<Grid container spacing={2}>
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Schedule:
												</Typography>
												<Typography variant="body1">
													{from} to {to}
												</Typography>
											</Grid>
											
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Time In:
												</Typography>
												<Typography variant="body1">
													{timeIn || '-'}
												</Typography>
											</Grid>
											
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Time Out:
												</Typography>
												<Typography variant="body1">
													{timeOut || '-'}
												</Typography>
											</Grid>
											
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Hours Worked:
												</Typography>
												<Typography variant="body1" sx={{ color: !timeIn && !timeOut && hourswork <= 0 ? 'red' : (Number(hourswork) <= 0 ? 'red' : 'inherit') }}>
													{!timeIn && !timeOut && hourswork <= 0 ? "Absent" : formatTableNumber(hourswork)}
												</Typography>
											</Grid>
											
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Tardiness (Minutes):
												</Typography>
												{isEditingDisabled ? (
													<Typography variant="body1" sx={{ color: tardy > 0 ? 'red' : 'inherit' }}>
														{formatTableNumber(tardy)}
													</Typography>
												) : (
													<Box sx={{ 
														backgroundColor: '#f5f5f5', 
														borderRadius: 1,
														border: '1px solid #e0e0e0',
														'&:hover': { backgroundColor: '#dedffa' }
													}}>
														<Editable 
															key={`${id}-hourstardy`} 
															text={tardy} 
															onEditText={(newText, diff) => {
																updateSchedValues(remarkId, {
																	hoursTardy: Number(newText)
																}, diff)

																let newTotals = computeTotals()
															
																saveScheduleValues({
																	uid: _id, 
																	date: date,
																	diff: diff, 
																	breaklistId: breaklistId, 
																	updates: {
																		hoursTardy: Number(newText)
																	}, 
																	totals: newTotals
																})

																onSave()
															}}
														/>
													</Box>
												)}
											</Grid>
											
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Overtime (Hours):
												</Typography>
												{isEditingDisabled ? (
													<Typography variant="body1">
														{formatTableNumber(ot)}
													</Typography>
												) : (
													<Box sx={{ 
														backgroundColor: '#f5f5f5', 
														borderRadius: 1,
														border: '1px solid #e0e0e0',
														'&:hover': { backgroundColor: '#dedffa' }
													}}>
														<Editable 
															key={`${id}-overtime`} 
															text={ot} 
															onEditText={(newText, diff) => {
																updateSchedValues(remarkId, {
																	overtime: Number(newText)
																}, diff)

																let newTotals = computeTotals()

																saveScheduleValues({
																	uid: _id, 
																	date: date,
																	breaklistId: breaklistId, 
																	diff: diff,
																	updates: {
																		otHours: Number(newText)
																	}, 
																	totals: newTotals
																})

																onSave()
															}}
														/>
													</Box>
												)}
											</Grid>
											
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Night Diff (Hours):
												</Typography>
												{isEditingDisabled ? (
													<Typography variant="body1">
														{formatTableNumber(nd)}
													</Typography>
												) : (
													<Box sx={{ 
														backgroundColor: '#f5f5f5', 
														borderRadius: 1,
														border: '1px solid #e0e0e0',
														'&:hover': { backgroundColor: '#dedffa' }
													}}>
														<Editable 
															key={`${id}-nightdiff`} 
															text={nd} 
															onEditText={(newText, diff) => {
																updateSchedValues(remarkId, {
																	nightdiff: Number(newText)
																}, diff)

																let newTotals = computeTotals()

																saveScheduleValues({
																	uid: _id, 
																	date: date, 
																	breaklistId: breaklistId,
																	diff: diff,
																	updates: {
																		nightdiff: Number(newText)
																	}, 
																	totals: newTotals
																})

																onSave()
															}}
														/>
													</Box>
												)}
											</Grid>
											
											<Grid item xs={6}>
												<Typography variant="body2" color="text.secondary">
													Rest Day (Hours):
												</Typography>
												{isEditingDisabled ? (
													<Typography variant="body1">
														{formatTableNumber(restday)}
													</Typography>
												) : (
													<Box sx={{ 
														backgroundColor: '#f5f5f5', 
														borderRadius: 1,
														border: '1px solid #e0e0e0',
														'&:hover': { backgroundColor: '#dedffa' }
													}}>
														<Editable 
															key={`${id}-rd`} 
															text={restday} 
															onEditText={(newText, diff) => {
																updateSchedValues(remarkId, {
																	rd: Number(newText)
																}, diff)

																let newTotals = computeTotals()

																saveScheduleValues({
																	uid: _id, 
																	date: date, 
																	breaklistId: breaklistId,
																	diff: diff,
																	updates: {
																		rd: Number(newText)
																	}, 
																	totals: newTotals
																})

																onSave()
															}}
														/>
													</Box>
												)}
											</Grid>
											
											<Grid item xs={12}>
												<Typography variant="body2" color="text.secondary">
													Remarks:
												</Typography>
												<Box sx={{ 
													backgroundColor: '#f5f5f5', 
													borderRadius: 1,
													border: '1px solid #e0e0e0',
													p: 1,
													mt: 0.5
												}}>
													<EditableText 
														key={`${id}-remarks`} 
														text={remarks || "No remarks"} 
														onEditText={(newText) => {
															saveRemarks(remarkId, newText)
														}}
													/>
												</Box>
											</Grid>
										</Grid>
									</CardContent>
								</Card>
							</Grid>
						);
					})}

					{breaklist.length === 0 && (
						<Grid item xs={12}>
							<Typography align="center">No records found</Typography>
						</Grid>
					)}

					<Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
						<Card sx={{ boxShadow: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
							<CardContent>
								<Typography variant="h6" gutterBottom>Totals</Typography>
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<Typography variant="body2" color="text.secondary">
											Hours Worked:
										</Typography>
										<Typography variant="body1" fontWeight="bold">
											{formatTableNumber(
												breaklist.reduce((acc, {hourswork}) => {
													const hours = Number(hourswork) || 0;
													return acc + (hours < 0 ? 0 : hours);
												}, 0)
											)}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="body2" color="text.secondary">
											Tardiness:
										</Typography>
										<Typography variant="body1" fontWeight="bold">
											{totals.hoursTardy}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="body2" color="text.secondary">
											Overtime:
										</Typography>
										<Typography variant="body1" fontWeight="bold">
											{totals.otHours}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="body2" color="text.secondary">
											Night Diff:
										</Typography>
										<Typography variant="body1" fontWeight="bold">
											{totals.nightdiff}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="body2" color="text.secondary">
											Rest Day:
										</Typography>
										<Typography variant="body1" fontWeight="bold">
											{totals.rd}
										</Typography>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				</Grid>

				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={breaklist.length}
					rowsPerPage={rowsPerPage}
					page={pageNumber}
					onPageChange={changePageNumber}
					onRowsPerPageChange={changeNumberOfRowsPerPage}
					labelRowsPerPage={"Rows per page"}
					labelDisplayedRows={({ from, to, count }) => {
						return `${from}-${to} of ${count}`;
					}}
				/>
			</>
		);
	}

	// Return existing table view for desktop screens
	return (
		<>
		<Table>
			<UserListHead
				order = {order}
				headLabel = {TABLEHEAD}
				rowCount = {breaklist?.length || 0}
				onRequestSort = {changeOrderBy}
			/>
			<TableBody>
			{breaklist.slice(pageNumber* rowsPerPage, pageNumber * rowsPerPage + rowsPerPage).map((row, index) => {

				const {
                	_id,
                    name,
                   	date,
                   	from,
                    to,
                    timeIn,
                    timeOut,
                    hoursTardy,
                    overtime,
                    nightdiff,
                    hourswork,
                    rd,
                    legalholiday,
                    specialholiday, 
                   remarks
                } = row

               	let id = `${_id}/${date}/${timeIn}/${timeOut}`

               	let remarkId = `${_id}-${date}-${timeIn}-${timeOut}`

                const formattedDate = moment(date).format('YYYY-MM-DD');

                let tardy = scheduleValuesOf(remarkId)?.['hoursTardy'] || hoursTardy || 0

                let ot = scheduleValuesOf(remarkId)?.['overtime'] || overtime  || 0

                let nd =  scheduleValuesOf(remarkId)?.['nightdiff'] ||  nightdiff || 0

                let restday =  scheduleValuesOf(remarkId)?.['rd'] || rd || 0

                // Check if editing should be disabled (absent or hourswork <= 0)
                const isEditingDisabled = !timeIn || !timeOut || !hourswork || Number(hourswork) <= 0
                
                // Updated to show text in red when hourswork is 0 or less
                let workHours = !timeIn && !timeOut && hourswork <= 0 ? 
                    <div style = {{color: 'red'}}>"Absent"</div> : 
                    Number(hourswork) <= 0 ? 
                    <div style = {{color: 'red'}}>{formatTableNumber(hourswork)}</div> : 
                    formatTableNumber(hourswork)

				return (
                    <TableRow
                        hover
                        key={`${_id}-${index}`}
                        tabIndex={-1}
                        role="checkbox"
                        sx={{
                            ...((remarks && remarks.toString().trim() !== '' && remarks.toString() !== 'no remarks') && {
                                backgroundColor: '#ffebee',
                                '&:hover': {
                                    backgroundColor: '#ffcdd2',
                                }
                            })
                        }}
                    >
                        <TableCell align="left">
                    		{formattedDate}
                        </TableCell>
                        <TableCell align="left">from {from} to {to}</TableCell>
                        <TableCell align="left">{timeIn}</TableCell>
                        <TableCell align="left">{timeOut}</TableCell>
                        <TableCell align="left">{workHours}</TableCell>
                        <TableCell align="left" style={{ color: tardy > 0 ? 'red' : 'inherit' }}>
                            {isEditingDisabled ? 
                              formatTableNumber(tardy) : 
                              <Editable key = {`${id}-hourstardy`} text = {tardy} onEditText = {(newText, diff) => {
                                updateSchedValues(remarkId, {
                                    hoursTardy: Number(newText)
                                }, diff)

                                let newTotals = computeTotals()
                             
                                saveScheduleValues({
                                    uid: _id, 
                                    date: date,
                                    diff: diff, 
                                    breaklistId: breaklistId, 
                                    updates: {
                                        hoursTardy: Number(newText)
                                    }, 
                                    totals: newTotals
                                })

                                onSave()
                              }}/>
                            }
	                        </TableCell>
                        <TableCell align="left">
                            {isEditingDisabled ? 
                              formatTableNumber(ot) : 
	                          <Editable key = {`${id}-overtime`} text = {ot} onEditText = {(newText, diff) => {
	                        	updateSchedValues(remarkId, {
                                	overtime: Number(newText)
                                }, diff)

                                let newTotals = computeTotals()

	                        	saveScheduleValues({
	                        		uid: _id, 
	                        		date: date,
	                        		breaklistId: breaklistId, 
	                        		diff: diff,
	                        		updates: {
	                        			otHours: Number(newText)
	                        		}, 
	                        		totals: newTotals
	                        	})

	                        	onSave()
	                          }}/>
                            }
                        </TableCell>
                        <TableCell align="left">
                            {isEditingDisabled ? 
                              formatTableNumber(nd) : 
                            <Editable key = {`${id}-nightdiff`} text = {nd} onEditText = {(newText, diff) => {
                        		updateSchedValues(remarkId, {
                                    nightdiff: Number(newText)
                                }, diff)

                                let newTotals = computeTotals()

	                        	saveScheduleValues({
	                        		uid: _id, 
	                        		date: date, 
	                        		breaklistId: breaklistId,
	                        		diff: diff,
	                        		updates: {
	                        			nightdiff: Number(newText)
	                        		}, 
	                        		totals: newTotals
	                        	})

								onSave()
	                            }}/>
                            }
                        </TableCell>
						<TableCell align="left">
                            {isEditingDisabled ? 
                              formatTableNumber(restday) : 
							<Editable key = {`${id}-rd`} text = {restday} onEditText = {(newText, diff) => {
								updateSchedValues(remarkId, {
                                	rd: Number(newText)
                                }, diff)

                                let newTotals = computeTotals()

	                        	saveScheduleValues({
	                        		uid: _id, 
	                        		date: date, 
	                        		breaklistId: breaklistId,
	                        		diff: diff,
	                        		updates: {
	                        			rd: Number(newText)
	                        		}, 
	                        		totals: newTotals
	                        	})

								onSave()
	                            }}/>
                            }
						</TableCell>
						<TableCell align="left">{legalholiday}</TableCell>
						<TableCell align="left">{specialholiday}</TableCell>
                        <TableCell align="left">
                        	<EditableText key = {`${id}-remarks`} text = {remarks || "no remarks"} onEditText = {(newText) => {
                        		saveRemarks(remarkId, newText)
                        	}}/>
                        </TableCell>
                    </TableRow>
                )
			})}

 					<TableRow
                        hover
                        tabIndex={-1}
                        role="checkbox"
						style={{backgroundColor: '#e0e0e0'}}
                    >
                        <TableCell align="left" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="left">-</TableCell>
                        <TableCell align="left">-</TableCell>
                        <TableCell align="left">-</TableCell>
                        <TableCell align="left" style = {{fontWeight: 'bold'}}>
                            {formatTableNumber(
                                breaklist.reduce((acc, {hourswork}) => {
                                    const hours = Number(hourswork) || 0;
                                    return acc + (hours < 0 ? 0 : hours);
                                }, 0)
                            )}
                        </TableCell>
                        <TableCell align="left" style = {{fontWeight: 'bold'}} >
		                	{computeTotals().hoursTardy}
	                    </TableCell>
                        <TableCell align="left" style = {{fontWeight: 'bold'}}>
	                      	{computeTotals().otHours} 
                        </TableCell>
                        <TableCell align="left" style = {{fontWeight: 'bold'}}>
                        	{computeTotals().nightdiff}	
                        </TableCell>
						<TableCell align="left" style = {{fontWeight: 'bold'}}>
							{computeTotals().rd}
						</TableCell>
						<TableCell align="left" style = {{fontWeight: 'bold'}}>0</TableCell>
						<TableCell align="left" style = {{fontWeight: 'bold'}}>0</TableCell>

                        <TableCell align="left">
                        	-
                        </TableCell>
                    </TableRow>
			</TableBody>
			</Table>

			<TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={breaklist.length}
                rowsPerPage={rowsPerPage}
                page={pageNumber}
                onPageChange={changePageNumber}
                onRowsPerPageChange={changeNumberOfRowsPerPage}
                labelRowsPerPage={"Rows per page"}
                labelDisplayedRows={({ from, to, count }) => {
				    return `${from}-${to} of ${count}`;
				}}
             />
		</>
	);
}

export default UserBreaklistTable 

